import { AppError, ErrorCode, ErrorContext, ErrorSeverity } from './types';
import { fromFirebaseAuthError } from './adapters/firebaseAuth';
import { fromFirestoreError } from './adapters/firestore';
import { fromFunctionsError } from './adapters/functions';
import { fromNetworkError } from './adapters/network';
import { fromPaymentError } from './adapters/payments';
import { fromStorageError } from './adapters/storage';
import { logError } from './logger';
import { notifyUser } from './notify';

export function getReadableError(err: unknown): string {
  // Preserve Axios HTTP messages
  const anyErr = err as any;
  if (anyErr?.response?.data?.message) return anyErr.response.data.message;
  if (anyErr?.message) return anyErr.message;
  // Firebase/Firestore
  if (anyErr?.code && anyErr?.message) return `${anyErr.code}: ${anyErr.message}`;
  return "Unexpected error";
}

type Known = 'auth'|'firestore'|'functions'|'network'|'payments'|'storage'|'validation';

export function normalizeError(err: any, domainHint?: Known): AppError {
  if (err instanceof AppError) return err;

  try {
    if (domainHint === 'auth') return fromFirebaseAuthError(err);
    if (domainHint === 'firestore') return fromFirestoreError(err);
    if (domainHint === 'functions') return fromFunctionsError(err);
    if (domainHint === 'network') return fromNetworkError(err);
    if (domainHint === 'payments') return fromPaymentError(err);
    if (domainHint === 'storage') return fromStorageError(err);
    // Heuristics fallback
    const code = String(err?.code ?? '');
    const msg = String(err?.message ?? '');
    if (code.startsWith('auth/')) return fromFirebaseAuthError(err);
    if (['permission-denied','not-found','already-exists','failed-precondition','unavailable'].includes(code) || msg.includes('Firestore')) {
      return fromFirestoreError(err);
    }
    if (['unavailable','invalid-argument'].includes(code)) return fromFunctionsError(err);
    if (code.startsWith('storage/')) return fromStorageError(err);
    if (msg.toLowerCase().includes('network')) return fromNetworkError(err);
  } catch { /* ignore */ }

  // Fallback: preserve the original error message if it's user-friendly
  const originalMessage = err?.message || '';
  return new AppError({ 
    code: ErrorCode.UNKNOWN, 
    domain: 'unknown', 
    cause: err, 
    severity: ErrorSeverity.Error,
    message: originalMessage // Preserve original message
  });
}

export function handleError(
  err: any,
  ctx?: ErrorContext,
  domainHint?: Known,
  opts?: { notify?: boolean; severityOverride?: ErrorSeverity }
): AppError {
  const appErr = normalizeError(err, domainHint);
  const merged = new AppError({
    code: appErr.code,
    domain: appErr.domain,
    cause: appErr.cause,
    ctx: { ...appErr.ctx, ...ctx },
    severity: opts?.severityOverride ?? appErr.severity,
    message: appErr.message, // Preserve the original message (may be user-friendly)
  });

  logError(merged);
  if (opts?.notify !== false) notifyUser(merged);
  return merged;
}

export async function withErrorHandling<T>(
  fn: () => Promise<T>,
  options?: { ctx?: ErrorContext; domainHint?: Known; notify?: boolean; severityOverride?: ErrorSeverity }
): Promise<T> {
  try {
    return await fn();
  } catch (e: any) {
    // Log Firebase error codes for debugging
    // SECURITY: Removed console.error to prevent information leakage
    // Preserve original error - don't swallow Firebase codes
    throw handleError(e, options?.ctx, options?.domainHint, { notify: options?.notify, severityOverride: options?.severityOverride });
  }
}

// Convenience functions for common patterns
export function handleAuthError(err: any, ctx?: ErrorContext): AppError {
  return handleError(err, ctx, 'auth');
}

export function handleFirestoreError(err: any, ctx?: ErrorContext): AppError {
  return handleError(err, ctx, 'firestore');
}

export function handleFunctionsError(err: any, ctx?: ErrorContext): AppError {
  return handleError(err, ctx, 'functions');
}

export function handleNetworkError(err: any, ctx?: ErrorContext): AppError {
  return handleError(err, ctx, 'network');
}

export function handlePaymentError(err: any, ctx?: ErrorContext): AppError {
  return handleError(err, ctx, 'payments');
}

export function handleStorageError(err: any, ctx?: ErrorContext): AppError {
  return handleError(err, ctx, 'storage');
}

export function handleValidationError(err: any, ctx?: ErrorContext): AppError {
  return handleError(err, ctx, 'validation');
}
